import React, { useState } from 'react';
import { Chip, Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { createEmptyKeyValue, type KeyValuePair } from './businessToolFormUtils';

export function SectionHeading({
  title,
  description,
  action,
}: Readonly<{
  title: string;
  description?: string;
  action?: React.ReactNode;
}>) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-default-500">{title}</h4>
        {description ? <p className="mt-0.5 text-xs text-default-400">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function KeyValueEditor({
  pairs,
  onChange,
  keyLabel,
  valueLabel,
  addLabel,
}: Readonly<{
  pairs: KeyValuePair[];
  onChange: (next: KeyValuePair[]) => void;
  keyLabel: string;
  valueLabel: string;
  addLabel: string;
}>) {
  return (
    <div className="flex flex-col gap-2">
      {pairs.map((pair, index) => (
        <div key={pair.id} className="flex items-center gap-2">
          <Input
            size="sm"
            variant="bordered"
            aria-label={keyLabel}
            placeholder={keyLabel}
            value={pair.key}
            onValueChange={(value) => {
              const next = [...pairs];
              next[index] = { ...pair, key: value };
              onChange(next);
            }}
            className="flex-1"
          />
          <Input
            size="sm"
            variant="bordered"
            aria-label={valueLabel}
            placeholder={valueLabel}
            value={pair.value}
            onValueChange={(value) => {
              const next = [...pairs];
              next[index] = { ...pair, value };
              onChange(next);
            }}
            className="flex-1"
          />
          <CtyButton
            isIconOnly
            size="sm"
            variant="light"
            color="danger"
            aria-label="Remove row"
            onPress={() => onChange(pairs.filter((item) => item.id !== pair.id))}
          >
            <Icon icon="mdi:close" width={16} height={16} />
          </CtyButton>
        </div>
      ))}
      <CtyButton
        size="sm"
        variant="flat"
        className="self-start"
        startContent={<Icon icon="mdi:plus" width={14} height={14} />}
        onPress={() => onChange([...pairs, createEmptyKeyValue()])}
      >
        {addLabel}
      </CtyButton>
    </div>
  );
}

export function ExamplesTagInput({
  values,
  onChange,
  placeholder,
}: Readonly<{
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}>) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const next = draft.trim();
    if (!next) return;
    if (!values.includes(next)) onChange([...values, next]);
    setDraft('');
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1 min-h-6">
        {values.map((example) => (
          <Chip
            key={example}
            size="sm"
            variant="flat"
            onClose={() => onChange(values.filter((item) => item !== example))}
          >
            {example}
          </Chip>
        ))}
      </div>
      <Input
        size="sm"
        variant="bordered"
        placeholder={placeholder}
        value={draft}
        onValueChange={setDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
      />
    </div>
  );
}

export function PlaceholderHelper({ placeholders }: Readonly<{ placeholders: string[] }>) {
  if (placeholders.length === 0) {
    return (
      <p className="text-xs text-default-400">
        Add parameters or pre-request extract fields to see available placeholders.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {placeholders.map((name) => (
        <Chip key={name} size="sm" variant="bordered" className="font-mono text-[11px]">
          {`{{${name}}}`}
        </Chip>
      ))}
    </div>
  );
}
